package com.tovtalk.presentation.viewmodel.profile

import com.tovtalk.data.local.TokenManager
import com.tovtalk.domain.entity.FeedItem
import com.tovtalk.domain.entity.Post
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.entity.buildFeedItems
import com.tovtalk.domain.entity.toggleLike
import com.tovtalk.domain.usecase.post.LikePostUseCase
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.domain.repository.AppRepository
import com.tovtalk.domain.repository.PostRepository
import com.tovtalk.domain.repository.UploadRepository
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.presentation.state.PagingState
import com.tovtalk.presentation.state.UiState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.launch
import kotlinx.coroutines.cancel

import com.tovtalk.presentation.ViewModel
import com.tovtalk.presentation.ScopedViewModel

/**
 * Profile ViewModel (Reels Only Architecture)
 */
class ProfileViewModel(
    private val userRepository: UserRepository,
    private val postRepository: PostRepository,
    private val uploadRepository: UploadRepository? = null,
    private val adsRepository: AdsRepository,
    private val appRepository: AppRepository,
    private val scope: CoroutineScope
) : ViewModel(), ScopedViewModel {

    /** Cancels the injected scope + every job/collector launched in it when the screen leaves. */
    override fun dispose() {
        scope.cancel()
    }
    private val _userState = MutableStateFlow<UiState<User>>(UiState.Idle)
    val userState: StateFlow<UiState<User>> = _userState.asStateFlow()
    
    private val _reelsState = MutableStateFlow(PagingState<Post>())
    val reelsState: StateFlow<PagingState<Post>> = _reelsState.asStateFlow()
    
    // Backward compatibility - points to reelsState
    val postsState: StateFlow<PagingState<Post>> = _reelsState.asStateFlow()
    
    private val _expiredReelsState = MutableStateFlow(PagingState<Post>())
    val expiredReelsState: StateFlow<PagingState<Post>> = _expiredReelsState.asStateFlow()
    
    private val _savedReelsState = MutableStateFlow(PagingState<Post>())
    val savedReelsState: StateFlow<PagingState<Post>> = _savedReelsState.asStateFlow()
    
    private val _selectedTab = MutableStateFlow(ProfileTab.REELS)
    val selectedTab: StateFlow<ProfileTab> = _selectedTab.asStateFlow()
    
    private val _uploadState = MutableStateFlow<UiState<String>>(UiState.Idle)
    val uploadState: StateFlow<UiState<String>> = _uploadState.asStateFlow()
    
    private val _uploadProgress = MutableStateFlow(0f)
    val uploadProgress: StateFlow<Float> = _uploadProgress.asStateFlow()

    private val _updateProfileState = MutableStateFlow<UiState<Unit>>(UiState.Idle)
    val updateProfileState: StateFlow<UiState<Unit>> = _updateProfileState.asStateFlow()

    private val _usernameError = MutableStateFlow<String?>(null)
    val usernameError: StateFlow<String?> = _usernameError.asStateFlow()

    // Track current user ID and read-only (kashrut only for read-only users)
    private val _currentUserId = MutableStateFlow<Int?>(null)
    val currentUserId: StateFlow<Int?> = _currentUserId.asStateFlow()
    private val _currentUserIsReadOnly = MutableStateFlow(false)
    val currentUserIsReadOnly: StateFlow<Boolean> = _currentUserIsReadOnly.asStateFlow()

    private val _showKashrutDialog = MutableStateFlow(false)
    val showKashrutDialog: StateFlow<Boolean> = _showKashrutDialog.asStateFlow()
    private val _kashrutDialogMessage = MutableStateFlow<String?>(null)
    val kashrutDialogMessage: StateFlow<String?> = _kashrutDialogMessage.asStateFlow()

    /** True when viewing another user's profile and server returned kashrut_restricted (reels hidden). */
    private val _profileReelsKashrutRestricted = MutableStateFlow(false)
    val profileReelsKashrutRestricted: StateFlow<Boolean> = _profileReelsKashrutRestricted.asStateFlow()

    /** True while a pull-to-refresh is in flight — lets the UI show the indicator without flashing the whole screen. */
    private val _isRefreshing = MutableStateFlow(false)
    val isRefreshing: StateFlow<Boolean> = _isRefreshing.asStateFlow()

    /** Sort for profile reels: recent | views | likes */
    private val _reelsSortBy = MutableStateFlow("recent")
    val reelsSortBy: StateFlow<String> = _reelsSortBy.asStateFlow()

    /** Whether the viewer has notifications enabled for this artist's new posts */
    private val _notifyArtistContent = MutableStateFlow(false)
    val notifyArtistContent: StateFlow<Boolean> = _notifyArtistContent.asStateFlow()

    private val _showAds = MutableStateFlow(false)
    val showAds: StateFlow<Boolean> = _showAds.asStateFlow()
    private val _adsEveryNPosts = MutableStateFlow(5)
    private val _feedItemsState = MutableStateFlow<List<FeedItem>>(emptyList())
    val feedItemsState: StateFlow<List<FeedItem>> = _feedItemsState.asStateFlow()
    private val _showProfileAds = MutableStateFlow(false)
    val showProfileAds: StateFlow<Boolean> = _showProfileAds.asStateFlow()
    private val _profileAdsEveryNItems = MutableStateFlow(9)
    val profileAdsEveryNItems: StateFlow<Int> = _profileAdsEveryNItems.asStateFlow()

    private val reelsCache = mutableMapOf<Int, List<Post>>()
    private var loadUserJob: kotlinx.coroutines.Job? = null
    private var reelsFetchJob: kotlinx.coroutines.Job? = null

    // Shared like path (same as HomeViewModel). Built locally from the already-injected
    // postRepository to avoid a DI change; LikePostUseCase is a thin wrapper.
    private val likePostUseCase = LikePostUseCase(postRepository)

    init {
        loadCurrentUserId()
        // Fetch ads frequency from server config
        scope.launch {
            val config = appRepository.getAppConfig()
            if (config is DomainResult.Success) {
                _adsEveryNPosts.value = config.data.adsEveryNPosts
                _profileAdsEveryNItems.value = config.data.profileAdsEveryNItems
            }
        }
        // Combine posts + sort + showAds + frequency to produce feedItems.
        // Sort is applied in-memory on the loaded items (no network roundtrip);
        // pagination keeps fetching in chronological order from the server.
        scope.launch {
            combine(_reelsState, _reelsSortBy, _showAds, _adsEveryNPosts) { reelState, sortBy, showAds, everyN ->
                val sorted = when (sortBy) {
                    "views" -> reelState.items.sortedByDescending { it.stats.views }
                    "likes" -> reelState.items.sortedByDescending { it.stats.likes }
                    else -> reelState.items.sortedByDescending { it.createdAt }
                }
                buildFeedItems(sorted, showAds, everyN)
            }.flowOn(Dispatchers.Default).collect { items ->
                _feedItemsState.value = items
            }
        }
        // Determine showAds from user
        scope.launch {
            userRepository.getCachedUser().collect { user ->
                val isGuest = TokenManager.isGuestMode
                _showAds.value = adsRepository.shouldShowFeedAds(
                    showAdsFlag = user?.showFeedAds,
                    isGuest = isGuest
                )
                _showProfileAds.value = adsRepository.shouldShowProfileAds(
                    showAdsFlag = user?.showProfileAds,
                    isGuest = isGuest
                )
            }
        }
        // For guests, getCachedUser() may never emit — set immediately
        if (TokenManager.isGuestMode) {
            _showAds.value = true
            _showProfileAds.value = true
        }
    }
    
    /**
     * Load current user ID for follow button visibility
     */
    private fun loadCurrentUserId() {
        scope.launch {
            when (val result = userRepository.getCurrentUser()) {
                is DomainResult.Success -> {
                    _currentUserId.value = result.data.id
                    _currentUserIsReadOnly.value = result.data.isReadOnly
                }
                else -> {}
            }
        }
    }
    
    fun loadUser(userId: Int?, username: String? = null, forceRefresh: Boolean = false) {
        loadUserJob?.cancel()
        loadUserJob = scope.launch {
            val cachedUser = (_userState.value as? UiState.Success)?.data
            val hasCachedData = cachedUser != null && when {
                userId != null -> cachedUser.id == userId
                username != null -> cachedUser.username == username
                else -> true // own profile - cached own profile is always valid
            }

            // Only show Loading if we have nothing to display for THIS user
            if (!hasCachedData) {
                _userState.value = UiState.Loading
            }

            val result = when {
                userId == null && username == null -> userRepository.getCurrentUser(forceRefresh)
                username != null -> userRepository.getUserByUsername(username, forceRefresh)
                else -> userRepository.getUserDetails(userId!!, forceRefresh)
            }

            when (result) {
                is DomainResult.Success -> {
                    val profileUser = result.data
                    if (userId == null && username == null) {
                        _currentUserId.value = profileUser.id
                    }
                    _userState.value = UiState.Success(profileUser)

                    // Load reels with stale-while-revalidate
                    if (!profileUser.isReadOnly) {
                        loadReelsWithCache(profileUser.id)
                    } else if (userId == null && username == null) {
                        _selectedTab.value = ProfileTab.SAVED
                        loadSavedReels(refresh = true)
                    }
                }
                is DomainResult.Error -> {
                    // Only show error if we have nothing cached
                    if (!hasCachedData) {
                        _userState.value = UiState.Error(result.message)
                    }
                    // If we have cached data, silently ignore the error
                }
                is DomainResult.Loading -> {}
            }
            _isRefreshing.value = false
        }
    }
    
    /** Show cached reels immediately (if available), then refresh from network in the background. */
    fun loadReelsWithCacheIfAvailable(userId: Int) = loadReelsWithCache(userId)

    private fun loadReelsWithCache(userId: Int) {
        // Show cached reels immediately if available
        reelsCache[userId]?.let { cached ->
            if (cached.isNotEmpty()) {
                _reelsState.value = PagingState(
                    items = cached,
                    isLoading = false,
                    hasMore = true
                )
            }
        }

        // Cancel previous fetch and start new one
        reelsFetchJob?.cancel()
        reelsFetchJob = scope.launch {
            val currentHasData = _reelsState.value.items.isNotEmpty()
            if (!currentHasData) {
                _reelsState.value = _reelsState.value.copy(isLoading = true)
            }

            when (val result = postRepository.getUserFeed(userId, cursor = null, limit = 15)) {
                is DomainResult.Success -> {
                    val feedPage = result.data
                    _profileReelsKashrutRestricted.value = feedPage.kashrutRestricted
                    reelsCache[userId] = feedPage.posts
                    _reelsState.value = PagingState(
                        items = feedPage.posts,
                        isLoading = false,
                        hasMore = feedPage.hasMore
                    )
                }
                is DomainResult.Error -> {
                    _profileReelsKashrutRestricted.value = false
                    // Only show error if we have no cached data
                    if (!currentHasData) {
                        _reelsState.value = _reelsState.value.copy(
                            isLoading = false,
                            error = result.message
                        )
                    }
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    // Backward compatibility
    fun loadPosts(userId: Int, refresh: Boolean = false) = loadReels(userId, refresh)
    
    fun loadReels(userId: Int, refresh: Boolean = false) {
        val currentState = _reelsState.value
        if (currentState.isLoading && !refresh) return
        if (!refresh && !currentState.hasMore) return
        if (refresh) _profileReelsKashrutRestricted.value = false

        scope.launch {
            _reelsState.value = currentState.copy(isLoading = true)

            val cursor = if (refresh) null else currentState.cursor

            when (val result = postRepository.getUserFeed(userId, cursor = cursor, limit = 15)) {
                is DomainResult.Success -> {
                    val feedPage = result.data
                    val currentItems = if (refresh) emptyList() else currentState.items
                    val existingIds = currentItems.mapTo(HashSet(currentItems.size)) { it.id }
                    val newItems = feedPage.posts.filter { it.id !in existingIds }
                    _profileReelsKashrutRestricted.value = feedPage.kashrutRestricted
                    val allItems = currentItems + newItems
                    _reelsState.value = PagingState(
                        items = allItems,
                        cursor = feedPage.nextCursor,
                        isLoading = false,
                        hasMore = feedPage.hasMore
                    )
                    // Update cache with first page when refreshing
                    if (refresh) {
                        reelsCache[userId] = newItems
                    }
                }
                is DomainResult.Error -> {
                    _profileReelsKashrutRestricted.value = false
                    // On refresh failure keep existing items visible
                    _reelsState.value = currentState.copy(
                        isLoading = false,
                        error = result.message
                    )
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    fun loadExpiredReels(userId: Int, refresh: Boolean = false) {
        // Expired reels endpoint removed in v2 API — no-op
    }
    
    fun loadSavedReels(refresh: Boolean = false) {
        val myId = _currentUserId.value ?: return
        val currentState = _savedReelsState.value
        if (currentState.isLoading && !refresh) return
        if (!refresh && !currentState.hasMore) return

        scope.launch {
            _savedReelsState.value = currentState.copy(isLoading = true)
            val cursor = if (refresh) null else currentState.cursor

            when (val result = postRepository.getSavedFeed(cursor = cursor, limit = 15)) {
                is DomainResult.Success -> {
                    val feedPage = result.data
                    val currentItems = if (refresh) emptyList() else currentState.items
                    val existingIds = currentItems.mapTo(HashSet(currentItems.size)) { it.id }
                    val newItems = feedPage.posts.filter { it.id !in existingIds }
                    _savedReelsState.value = PagingState(
                        items = currentItems + newItems,
                        cursor = feedPage.nextCursor,
                        isLoading = false,
                        hasMore = feedPage.hasMore
                    )
                }
                is DomainResult.Error -> {
                    // On refresh failure keep existing items visible
                    _savedReelsState.value = currentState.copy(
                        isLoading = false,
                        error = result.message
                    )
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    fun makeReelPermanent(postId: Int) {
        // makeReelPermanent removed in v2 API — no-op
    }
    
    fun followUser(userId: Int) {
        updateFollowingState(true)
        scope.launch {
            when (val result = userRepository.followUser(userId)) {
                is DomainResult.Success -> {}
                is DomainResult.Error -> {
                    if (result.isKashrutNotAllowed) {
                        updateFollowingState(false)
                        _kashrutDialogMessage.value = result.message
                        _showKashrutDialog.value = true
                    }
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    fun loadFollowPreferences(userId: Int) {
        scope.launch {
            when (val result = userRepository.getFollowPreferences(userId)) {
                is DomainResult.Success -> {
                    _notifyArtistContent.value = result.data.notifyNewPosts
                }
                is DomainResult.Error -> {
                    _notifyArtistContent.value = false
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    fun toggleNotifyArtistContent(userId: Int) {
        val newValue = !_notifyArtistContent.value
        _notifyArtistContent.value = newValue // optimistic
        scope.launch {
            when (userRepository.updateFollowPreferences(userId, notifyNewPosts = newValue)) {
                is DomainResult.Error -> {
                    _notifyArtistContent.value = !newValue // revert
                }
                else -> {}
            }
        }
    }

    fun dismissKashrutDialog() {
        _showKashrutDialog.value = false
        _kashrutDialogMessage.value = null
    }
    
    fun blockUser(userId: Int, onSuccess: () -> Unit = {}) {
        scope.launch {
            when (userRepository.blockUser(userId)) {
                is DomainResult.Success -> onSuccess()
                is DomainResult.Error -> {}
                is DomainResult.Loading -> {}
            }
        }
    }

    fun unfollowUser(userId: Int) {
        // Optimistic update - update UI immediately
        updateFollowingState(false)
        
        scope.launch {
            when (userRepository.unfollowUser(userId)) {
                is DomainResult.Success -> {
                    // Already updated optimistically
                }
                is DomainResult.Error -> {
                    // If error (e.g., not following), state is likely correct anyway
                    // Could revert here if needed
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    /**
     * Update the following state and follower count in userState
     */
    private fun updateFollowingState(isFollowing: Boolean) {
        val currentState = _userState.value
        if (currentState is UiState.Success) {
            val user = currentState.data
            val newFollowerCount = if (isFollowing) user.followerCount + 1 else (user.followerCount - 1).coerceAtLeast(0)
            _userState.value = UiState.Success(
                user.copy(isFollowing = isFollowing, followerCount = newFollowerCount)
            )
        }
    }
    
    fun setTab(tab: ProfileTab) {
        val profileUser = (_userState.value as? UiState.Success)?.data
        if (profileUser?.isReadOnly == true && tab != ProfileTab.SAVED) return
        _selectedTab.value = tab
        when (tab) {
            ProfileTab.EXPIRED -> (_userState.value as? UiState.Success)?.data?.id?.let { loadExpiredReels(it, refresh = true) }
            ProfileTab.SAVED -> loadSavedReels(refresh = true)
            ProfileTab.REELS -> {}
        }
    }

    fun setReelsSortBy(sortBy: String) {
        if (sortBy in listOf("recent", "views", "likes")) {
            _reelsSortBy.value = sortBy
        }
    }
    
    
    /**
     * Calculate if username can be changed based on cooldown.
     * @param usernameChangedAt parsed Instant or null (null = never changed, allowed)
     * @param cooldownDays from AppConfig
     * @return Pair(canChange, daysRemaining)
     */
    fun calculateUsernameChangeability(usernameChangedAt: kotlin.time.Instant?, cooldownDays: Int): Pair<Boolean, Int> {
        if (usernameChangedAt == null) return Pair(true, 0)
        return try {
            val now = kotlin.time.Clock.System.now()
            val elapsed = now - usernameChangedAt
            val daysSinceChange = elapsed.inWholeDays.toInt()
            if (daysSinceChange >= cooldownDays) {
                Pair(true, 0)
            } else {
                Pair(false, cooldownDays - daysSinceChange)
            }
        } catch (_: Exception) {
            Pair(true, 0)
        }
    }

    fun clearUsernameError() {
        _usernameError.value = null
    }

    fun updateProfile(
        fullname: String? = null,
        bio: String? = null,
        username: String? = null,
        instagramId: String? = null,
        whatsappStatusId: String? = null,
        youtubeChannelId: String? = null,
        autoPullInstagram: Boolean? = null,
        autoPullWhatsappStatus: Boolean? = null,
        autoPullYoutube: Boolean? = null,
        autoUpdateProfileInstagram: Boolean? = null,
        autoUpdateProfileWhatsappStatus: Boolean? = null,
        autoUpdateProfileYoutube: Boolean? = null,
        uploadCategoryId: Int? = null,
        receiveMessage: Boolean? = null
    ) {
        scope.launch {
            _updateProfileState.value = UiState.Loading
            val updates = mutableMapOf<String, Any>()
            fullname?.let { updates["fullname"] = it }
            bio?.let { updates["bio"] = it }
            username?.let { updates["username"] = it }
            uploadCategoryId?.let { updates["upload_category_id"] = it }
            receiveMessage?.let { updates["receive_message"] = it }
            if (instagramId != null) updates["instagram_id"] = instagramId
            if (whatsappStatusId != null) updates["whatsapp_status_id"] = whatsappStatusId
            if (youtubeChannelId != null) updates["youtube_channel_id"] = youtubeChannelId
            if (autoPullInstagram != null) updates["auto_pull_instagram"] = autoPullInstagram
            if (autoPullWhatsappStatus != null) updates["auto_pull_whatsapp_status"] = autoPullWhatsappStatus
            if (autoPullYoutube != null) updates["auto_pull_youtube"] = autoPullYoutube
            if (autoUpdateProfileInstagram != null) updates["auto_update_profile_instagram"] = autoUpdateProfileInstagram
            if (autoUpdateProfileWhatsappStatus != null) updates["auto_update_profile_whatsapp_status"] = autoUpdateProfileWhatsappStatus
            if (autoUpdateProfileYoutube != null) updates["auto_update_profile_youtube"] = autoUpdateProfileYoutube

            when (val result = userRepository.updateProfile(updates)) {
                is DomainResult.Success -> {
                    _updateProfileState.value = UiState.Success(Unit)
                    loadUser(null)
                }
                is DomainResult.Error -> {
                    _updateProfileState.value = UiState.Error(result.message)
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    fun updateNotificationPreference(key: String, value: Boolean) {
        scope.launch {
            val updates: Map<String, Any> = mapOf(key to value)
            userRepository.updateProfile(updates)
        }
    }
    
    /**
     * העלאת תמונת פרופיל חדשה
     */
    fun uploadProfilePhoto(bytes: ByteArray, filename: String) {
        if (uploadRepository == null) {
            _uploadState.value = UiState.Error("Upload not available")
            return
        }
        
        scope.launch {
            _uploadState.value = UiState.Loading
            _uploadProgress.value = 0f
            
            when (val result = uploadRepository.uploadFile(
                bytes = bytes,
                filename = filename,
                category = "profiles",
                onProgress = { progress ->
                    _uploadProgress.value = progress
                }
            )) {
                is DomainResult.Success -> {
                    // עדכון הפרופיל עם ה-URL החדש
                    val updates = mapOf("profile_photo" to result.data.path)
                    when (val updateResult = userRepository.updateProfile(updates)) {
                        is DomainResult.Success -> {
                            _uploadState.value = UiState.Success(result.data.url)
                            // Use the updated user from the response instead of re-fetching
                            _userState.value = UiState.Success(updateResult.data)
                            userRepository.cacheUser(updateResult.data)
                        }
                        is DomainResult.Error -> {
                            com.tovtalk.utils.logger.Logger.e("profile photo update (after upload) failed: ${updateResult.message}", "ProfileViewModel")
                            _uploadState.value = UiState.Error(updateResult.message)
                        }
                        is DomainResult.Loading -> {}
                    }
                }
                is DomainResult.Error -> {
                    com.tovtalk.utils.logger.Logger.e("profile photo upload failed: ${result.message}", "ProfileViewModel")
                    _uploadState.value = UiState.Error(result.message)
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    /**
     * איפוס מצב ההעלאה
     */
    fun resetUploadState() {
        _uploadState.value = UiState.Idle
        _uploadProgress.value = 0f
    }

    fun resetUpdateProfileState() {
        _updateProfileState.value = UiState.Idle
    }
    
    /**
     * Increment comment count for a post (after adding a comment)
     */
    fun incrementCommentCount(postId: Int) {
        _reelsState.value = _reelsState.value.copy(
            items = _reelsState.value.items.map { post ->
                if (post.id == postId) {
                    post.copy(stats = post.stats.copy(comments = post.stats.comments + 1))
                } else {
                    post
                }
            }
        )
    }
    
    /**
     * Like/Unlike a post — routed through the shared LikePostUseCase + Post.toggleLike helper
     * so behavior matches HomeViewModel (non-optimistic: update only on success).
     */
    fun likePost(postId: Int, isCurrentlyLiked: Boolean) {
        scope.launch {
            when (likePostUseCase(postId, isCurrentlyLiked)) {
                is DomainResult.Success -> {
                    _reelsState.value = _reelsState.value.copy(
                        items = _reelsState.value.items.toggleLike(postId, isCurrentlyLiked)
                    )
                }
                is DomainResult.Error -> {
                    // Handle error silently
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    /**
     * Save/Unsave reel. Optimistic update: UI toggles immediately; on API error we revert.
     */
    fun savePost(postId: Int, isSaved: Boolean) {
        scope.launch {
            val previousItems = _reelsState.value.items
            // Optimistic update
            val optimisticItems = previousItems.map { post ->
                if (post.id == postId) {
                    post.copy(
                        viewerState = post.viewerState.copy(isSaved = !isSaved),
                        stats = post.stats.copy(saves = if (isSaved) post.stats.saves - 1 else post.stats.saves + 1)
                    )
                } else post
            }
            _reelsState.value = _reelsState.value.copy(items = optimisticItems)

            val result = if (isSaved) postRepository.unsavePost(postId) else postRepository.savePost(postId)
            when (result) {
                is DomainResult.Success -> { /* already updated optimistically */ }
                is DomainResult.Error -> {
                    // Revert optimistic update
                    _reelsState.value = _reelsState.value.copy(items = previousItems)
                }
                is DomainResult.Loading -> {}
            }
        }
    }

    fun deletePost(postId: Int) = deleteReel(postId)
    
    fun deleteReel(postId: Int) {
        scope.launch {
            when (val result = postRepository.deletePost(postId)) {
                is DomainResult.Success -> {
                    // Remove from reels state
                    _reelsState.value = _reelsState.value.copy(
                        items = _reelsState.value.items.filter { it.id != postId }
                    )
                    // Also remove from expired and saved if present
                    _expiredReelsState.value = _expiredReelsState.value.copy(
                        items = _expiredReelsState.value.items.filter { it.id != postId }
                    )
                    _savedReelsState.value = _savedReelsState.value.copy(
                        items = _savedReelsState.value.items.filter { it.id != postId }
                    )
                }
                is DomainResult.Error -> {
                    // Handle error if needed
                }
                is DomainResult.Loading -> {}
            }
        }
    }
    
    fun logout(onSuccess: () -> Unit) {
        scope.launch {
            userRepository.logout()
            onSuccess()
        }
    }
}

enum class ProfileTab {
    REELS,
    EXPIRED,  // Only visible to profile owner
    SAVED     // Only visible to profile owner - saved reels
}
